import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FormPageLayout } from '../../../../components/layout/FormPageLayout';
import { Button } from '../../../../components/ui/Button';
import { Input, FormField } from '../../../../components/ui/FormField';
import { useToast } from '../../../../components/ui/Toast';
import { usePageTitle } from '../../../../lib/hooks';
import { defineFormPage, useFormPage } from '@repo/form';
import { z } from 'zod';
import { Banner } from '../../../../components/ui/Banner';
import { useCreateAreaGroup } from '../../../../features/settings/network/areas.api';

export const Route = createFileRoute('/_auth/settings/areas/new-group')({
  component: CreateAreaGroupPage,
});

const groupFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  code: z.string().min(1, 'Code is required'),
});

type FormValues = z.infer<typeof groupFormSchema>;

const groupFormConfig = defineFormPage<FormValues>({
  resource: 'area-groups',
  defaultValues: {
    name: '',
    code: '',
  },
  schema: groupFormSchema,
});

function CreateAreaGroupPage() {
  usePageTitle('Add Area Group');
  const navigate = useNavigate();
  const { toast } = useToast();
  const createAreaGroupMutation = useCreateAreaGroup();

  const {
    form,
    handleSubmit,
    fieldError: fe,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(groupFormConfig, { mutateAsync: (values) => createAreaGroupMutation.mutateAsync(values) }, {
    routeId: 'settings/areas/new-group',
  });

  const onFormSubmit = async (e: React.FormEvent) => {
    await handleSubmit(e);
    clearDraft();
    toast({ type: 'success', title: 'Success', description: 'Area Group created successfully.' });
    void navigate({ to: '/settings/areas' });
  };

  return (
    <FormPageLayout
      title="Add Area Group"
      subtitle="Group regional branches, customers, or territories under a single geographic banner."
      backTo="/settings/areas"
      variant="card"
    >
      <form onSubmit={onFormSubmit} className="space-y-4">
            {hasDraft && (
              <Banner
                variant="info"
                title="Restore Draft"
                description="You have an unsaved draft from a previous session. Would you like to restore it?"
                action={{
                  label: 'Restore',
                  onClick: () => {
                    const draft = restoreDraft();
                    if (draft) {
                      Object.entries(draft).forEach(([key, val]) => {
                        form.setFieldValue(key as never, val as never);
                      });
                    }
                    dismissDraft();
                  },
                }}
                secondaryAction={{
                  label: 'Discard',
                  onClick: () => {
                    clearDraft();
                  },
                }}
              />
            )}

            <form.Field
              name="name"
              children={(field: any) => (
                <FormField label="Area Group Name" required error={fe(field.state.meta)}>
                  <Input
                    placeholder="e.g. West Hub Division"
                    value={field.state.value}
                    onChange={e => field.handleChange(e.target.value)}
                  />
                </FormField>
              )}
            />

            <form.Field
              name="code"
              children={(field: any) => (
                <FormField label="Unique Code" required error={fe(field.state.meta)}>
                  <Input
                    placeholder="e.g. WH-04"
                    value={field.state.value}
                    onChange={e => field.handleChange(e.target.value)}
                  />
                </FormField>
              )}
            />

            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button type="button" variant="secondary" onClick={() => void navigate({ to: '/settings/areas' })}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Save Group
              </Button>
            </div>
          </form>
    </FormPageLayout>
  );
}
